import { Button, Input, Spin, Tag, Tooltip, message as antdMessage } from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  InboxOutlined,
  MailOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { ContactAvatar } from "./ContactAvatar";
import { AiDraftPreview } from "./AiDraftPreview";
import { AttachmentChips } from "./AttachmentChips";
import { HtmlBody } from "./HtmlBody";
import {
  archiveThread,
  getThread,
  markThreadRead,
  markThreadUnread,
} from "../api/threads";
import type { ThreadDetail as ThreadDetailT, ThreadMessage } from "../api/threads";
import { sendMessage } from "../api/messages";
import { formatDateTime } from "../utils/time";
import { useStreamingDraft } from "../hooks/useStreamingDraft";

type Props = {
  threadId: string | null;
  onClose?: () => void;
  showBack?: boolean;
  onAfterSend?: () => void;
  onAfterAction?: () => void;
};

export function ThreadDetail({ threadId, onClose, showBack, onAfterSend, onAfterAction }: Props) {
  const [thread, setThread] = useState<ThreadDetailT | null>(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { state: draftState, start: startDraft, reset: resetDraft } = useStreamingDraft();

  useEffect(() => {
    if (!threadId) {
      setThread(null);
      resetDraft();
      return;
    }
    resetDraft();
    setLoading(true);
    let cancelled = false;
    getThread(threadId)
      .then(({ thread }) => {
        if (!cancelled) {
          setThread(thread);
          setReply("");
        }
      })
      .catch(() => {
        if (!cancelled) setThread(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [thread?.id, thread?.messages.length]);

  const lastInbound = thread ? [...thread.messages].reverse().find((m) => !m.isSent) : null;
  const lastMessage = thread ? thread.messages[thread.messages.length - 1] ?? null : null;
  // AI / 发送的锚定消息: 优先用最近一封对方来信(回复场景); 没有就用我们最后一封发出的(跟进场景)
  const anchorMessage = lastInbound ?? lastMessage;
  const isFollowupMode = !lastInbound && lastMessage?.isSent === true;

  const onAiDraft = async () => {
    if (!thread || !anchorMessage) {
      antdMessage.warning("线索为空,无法起草");
      return;
    }
    await startDraft(thread.id, anchorMessage.id);
  };

  const onSend = async () => {
    if (!thread || !reply.trim()) return;
    if (!anchorMessage) {
      antdMessage.warning("线索为空,无法发送");
      return;
    }
    const recipient = anchorMessage.isSent
      ? anchorMessage.toEmails[0]
      : anchorMessage.fromEmail;
    if (!recipient) {
      antdMessage.warning("缺少收件人");
      return;
    }
    setSending(true);
    try {
      await sendMessage({
        to: [recipient],
        subject: thread.subject?.startsWith("Re:") ? thread.subject : `Re: ${thread.subject ?? ""}`,
        body: reply,
        threadId: thread.gmailThreadId,
        inReplyToGmailMessageId: anchorMessage.gmailMessageId,
      });
      setReply("");
      antdMessage.success(isFollowupMode ? "跟进已发送" : "已发送");
      onAfterSend?.();
    } catch {
      antdMessage.error("发送失败,请稍后重试");
    } finally {
      setSending(false);
    }
  };

  if (!threadId) {
    return (
      <div
        style={{
          height: "100%",
          display: "grid",
          placeItems: "center",
          color: "var(--ant-color-text-secondary)",
          padding: 32,
          textAlign: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>选择一封邮件查看</div>
          <div style={{ fontSize: 15 }}>左侧是按时间排序的邮件列表,点击任一条进入详情。</div>
        </div>
      </div>
    );
  }

  if (loading || !thread) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center" }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: "1px solid var(--ant-color-border)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "var(--ant-color-bg-container)",
        }}
      >
        {showBack ? (
          <Button type="text" shape="circle" icon={<ArrowLeftOutlined />} onClick={onClose} />
        ) : null}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 19,
              fontWeight: 700,
              letterSpacing: -0.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {thread.subject || "(无主题)"}
          </h2>
          <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)", marginTop: 2 }}>
            {thread.messages.length} 封消息
          </div>
        </div>
        <ThreadActions
          thread={thread}
          onAction={async () => {
            const { thread: refreshed } = await getThread(thread.id);
            setThread(refreshed);
            onAfterAction?.();
          }}
        />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {thread.messages.map((m) => (
            <MessageBubble key={m.id} m={m} />
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div
        style={{
          padding: 16,
          borderTop: "1px solid var(--ant-color-border)",
          background: "var(--ant-color-bg-container)",
          paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
        }}
      >
        <AiDraftPreview
          state={draftState}
          onSent={() => {
            resetDraft();
            onAfterSend?.();
          }}
          onDismissed={resetDraft}
        />
        <Input.TextArea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder={isFollowupMode ? "撰写跟进..." : "快速回复..."}
          style={{ borderRadius: 12, fontSize: 16 }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
          <Tooltip
            title={
              isFollowupMode
                ? "对方还没回复,让 AI 起草一封温和的跟进"
                : "让 AI 根据你设置的中心思想起草一份回复"
            }
          >
            <Button
              icon={<ThunderboltOutlined />}
              onClick={onAiDraft}
              disabled={!anchorMessage || draftState.status === "starting" || draftState.status === "streaming"}
              loading={draftState.status === "starting" || draftState.status === "streaming"}
            >
              {isFollowupMode ? "AI 起草跟进" : "AI 起草"}
            </Button>
          </Tooltip>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            disabled={!reply.trim()}
            onClick={onSend}
          >
            发送
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m }: { m: ThreadMessage }) {
  const fromMe = m.isSent;
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexDirection: fromMe ? "row-reverse" : "row",
      }}
    >
      <ContactAvatar email={m.from.email} name={m.from.name} tier={m.from.tier} size={36} />
      <div style={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
        <div
          style={{
            display: "flex",
            justifyContent: fromMe ? "flex-end" : "space-between",
            gap: 8,
            marginBottom: 4,
            fontSize: 14,
            color: "var(--ant-color-text-secondary)",
          }}
        >
          <span style={{ fontWeight: 500, color: "var(--ant-color-text)" }}>
            {m.from.name || m.from.email}
          </span>
          {m.from.tier === "important" && !fromMe ? (
            <Tag color="gold" style={{ marginRight: 0 }}>
              重要
            </Tag>
          ) : null}
          <span>{formatDateTime(m.receivedAt)}</span>
        </div>
        <div
          style={{
            background: fromMe
              ? "color-mix(in srgb, var(--ant-color-primary) 14%, transparent)"
              : "var(--ant-color-bg-container)",
            border: "1px solid var(--ant-color-border)",
            padding: m.bodyHtml ? "8px 10px" : "12px 14px",
            borderRadius: 12,
            wordBreak: "break-word",
            fontSize: 16,
            lineHeight: 1.6,
            ...(m.bodyHtml ? {} : { whiteSpace: "pre-wrap" as const }),
          }}
        >
          {m.bodyHtml ? (
            <HtmlBody html={m.bodyHtml} messageId={m.id} attachments={m.attachments} />
          ) : (
            m.body || m.snippet || "(空)"
          )}
          <AttachmentChips messageId={m.id} attachments={m.attachments} />
        </div>
      </div>
    </div>
  );
}

function ThreadActions({
  thread,
  onAction,
}: {
  thread: ThreadDetailT;
  onAction: () => Promise<void>;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const hasUnread = thread.messages.some((m) => !m.isRead);

  const wrap = (label: string, fn: () => Promise<unknown>) => async () => {
    setPending(label);
    try {
      await fn();
      await onAction();
    } catch {
      antdMessage.error("操作失败");
    } finally {
      setPending(null);
    }
  };

  return (
    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
      <Tooltip title={hasUnread ? "标记已读" : "标记未读"}>
        <Button
          type="text"
          shape="circle"
          loading={pending === "read"}
          icon={hasUnread ? <CheckOutlined /> : <MailOutlined />}
          onClick={
            hasUnread
              ? wrap("read", () => markThreadRead(thread.id))
              : wrap("read", () => markThreadUnread(thread.id))
          }
          aria-label={hasUnread ? "标记已读" : "标记未读"}
        />
      </Tooltip>
      <Tooltip title="归档(移出收件箱)">
        <Button
          type="text"
          shape="circle"
          loading={pending === "archive"}
          icon={<InboxOutlined />}
          onClick={wrap("archive", async () => {
            await archiveThread(thread.id);
            antdMessage.success("已归档");
          })}
          aria-label="归档"
        />
      </Tooltip>
    </div>
  );
}
